import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as mealPlansApi from './meal-plans.api';

import type {
  CreateMealPlanRequest,
  MealPlanListQuery,
  UpdateMealPlanRequest,
} from '@utazzo/shared';

const QK = {
  list: (query: MealPlanListQuery) => ['meal-plans', 'list', query] as const,
  detail: (id: string) => ['meal-plans', 'detail', id] as const,
};

export const useMealPlansList = (query: MealPlanListQuery) =>
  useQuery({
    queryKey: QK.list(query),
    queryFn: () => mealPlansApi.listMealPlans(query),
    staleTime: 30_000,
  });

export const useCreateMealPlan = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateMealPlanRequest) => mealPlansApi.createMealPlan(payload),
    onSuccess: (mp) => {
      qc.invalidateQueries({ queryKey: ['meal-plans', 'list'] });
      toast.success(`Added ${mp.name}`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to add meal plan'),
  });
};

export const useUpdateMealPlan = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateMealPlanRequest }) =>
      mealPlansApi.updateMealPlan(id, payload),
    onSuccess: (mp) => {
      qc.invalidateQueries({ queryKey: ['meal-plans', 'list'] });
      qc.invalidateQueries({ queryKey: QK.detail(mp.id) });
      toast.success('Meal plan updated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update meal plan'),
  });
};

export const useDeleteMealPlan = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => mealPlansApi.deleteMealPlan(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['meal-plans', 'list'] });
      toast.success('Meal plan deleted');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete meal plan'),
  });
};

export const useBulkDeleteMealPlans = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => mealPlansApi.bulkDeleteMealPlans(ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['meal-plans'] }),
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete meal plans'),
  });
};

export const useBulkSetMealPlansStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, isActive }: { ids: string[]; isActive: boolean }) =>
      mealPlansApi.bulkSetMealPlansStatus(ids, isActive),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['meal-plans'] }),
    onError: (err: unknown) =>
      toast.error(extractMessage(err) ?? 'Failed to update meal plan status'),
  });
};

const extractMessage = (err: unknown): string | null => {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; details?: { message?: unknown } };
    if (e.details && typeof e.details.message === 'string') return e.details.message;
    if (typeof e.message === 'string') return e.message;
  }
  if (err instanceof Error) return err.message;
  return null;
};
